import React from 'react';
import { Grid, Chip, TextField, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import InputAdornment from '@material-ui/core/InputAdornment';
import OutlinedSelect from '../../../Components/OutlinedSelect';
import PhoneInputField from '../../../Components/PhoneInputField';
import ProfilePhotoField from '../../../Components/ProfilePhotoField';
import ContactPhoneOutlinedIcon from '@material-ui/icons/ContactPhoneOutlined';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import { CustomerServices } from '../../../Services/CustomerServices';
import { USER_ROLES } from 'utils/constants';
import {
    ADDITIONAL_PROFILE_FIELDS,
    EMPLOYMENT_STATUS_OPTIONS,
    INTEGER_PROFILE_KEYS,
    JOB_PROFILE_FIELDS,
    LEARNER_CATEGORY_OPTIONS,
    formatLocalIsoDate,
    isProfileImageDataUri,
    isReskillingEmployed,
} from 'utils/userProfileFields';
import { provinceOptions, withCurrentOption, normalizeToOption } from 'utils/locationOptions';
import { toast } from 'react-toastify';
import variables from '../../../Sys/variable.scss';

const customerServices = new CustomerServices();

const useProfileStyles = makeStyles((theme) => ({
    basicLayout: {
        display: 'grid',
        gridTemplateColumns: '172px minmax(0, 1fr)',
        gap: theme.spacing(3),
        alignItems: 'start',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: theme.spacing(2),
        },
    },
    fieldsCol: {
        minWidth: 0,
    },
    section: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2.5),
        border: '1px solid var(--dm-border, #e7ebf0)',
        borderRadius: 12,
        background: 'var(--contentColor, #fff)',
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
    },
    sectionIcon: {
        width: 32,
        height: 32,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 10,
        color: theme.palette.primary.main,
        backgroundColor: 'rgba(61, 125, 221, 0.1)',
        '& svg': { fontSize: 18 },
    },
    sectionTitle: {
        fontWeight: 700,
        fontSize: '0.9375rem',
        lineHeight: 1.35,
        color: variables.labelcolor || 'var(--dm-text-strong, #263548)',
    },
    sectionHint: {
        fontSize: '0.75rem',
        lineHeight: 1.45,
        color: 'var(--dm-muted, #647184)',
        marginTop: 2,
    },
    sectionFields: {
        marginTop: '12px !important',
    },
}));

const SectionHeader = ({ classes, icon, title, hint }) => (
    <div className={classes.sectionHeader}>
        <span className={classes.sectionIcon} aria-hidden="true">{icon}</span>
        <div>
            <Typography component="h4" className={classes.sectionTitle}>{title}</Typography>
            {hint ? <Typography className={classes.sectionHint}>{hint}</Typography> : null}
        </div>
    </div>
);

const TEXT_PROFILE_FIELDS = ADDITIONAL_PROFILE_FIELDS.filter((field) => (
    !['contact_no', 'country', 'city', 'province'].includes(field.name)
));

export default function CreateUserForm({
    formState,
    handleInputChange,
    handleChangeCustomer,
    companyList,
    customers,
    roleList,
    userDetails,
    userNameDisable,
    emailDisable,
    projectList,
    isOnbaording,
    showProject,
    showPassword,
    onGeneratePassword,
    showProfileFields,
    handleFieldsChange,
    adminFieldsLocked,
    programmeType,
    programmeTypeLabel,
    programmeProfileFields = [],
}) {
    const profileClasses = useProfileStyles();
    const [countryList, setCountryList] = React.useState([]);

    const setFields = (patch) => {
        if (handleFieldsChange) {
            handleFieldsChange(patch);
            return;
        }
        Object.keys(patch).forEach((name) => {
            handleInputChange({ target: { name, value: patch[name] } });
        });
    };

    React.useEffect(() => {
        if (!showProfileFields) return undefined;
        let active = true;
        customerServices.getCounties()
            .then((result) => {
                if (active) setCountryList(Array.isArray(result?.country_list) ? result.country_list : []);
            })
            .catch(() => {
                if (active) setCountryList([]);
            });
        return () => {
            active = false;
        };
    }, [showProfileFields]);

    const handleFieldChange = (evt) => {
        const { name, value } = evt.target;
        if (INTEGER_PROFILE_KEYS.includes(name) && value !== '' && !/^\d+$/.test(value)) {
            return;
        }
        handleInputChange(evt);
    };

    const photoInputId = React.useRef(`profile-image-${Math.random().toString(36).slice(2)}`).current;
    const showJobFields = !programmeType
        && isReskillingEmployed(formState.learner_category, formState.employment_status);

    const handlePhoto = (event) => {
        const input = event.target;
        const file = input.files && input.files[0];
        input.value = '';
        if (!file) return;
        if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
            toast('Profile photo must be a JPEG or PNG.', { type: 'error' });
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            if (!isProfileImageDataUri(reader.result)) {
                toast('Profile photo must be a JPEG or PNG.', { type: 'error' });
                return;
            }
            handleInputChange({ target: { name: 'profile_image', value: reader.result } });
        };
        reader.readAsDataURL(file);
    };

    const countryOptions = withCurrentOption(countryList, formState.country);
    const provinces = withCurrentOption(provinceOptions(formState.country), formState.province);

    const handleCountryChange = (nextValue) => {
        const next = nextValue || '';
        if (next !== (formState.country || '')) {
            setFields({ country: next, province: '', city: '' });
            return;
        }
        setFields({ country: next });
    };

    const handleProvinceChange = (nextValue) => {
        const next = nextValue || '';
        if (next !== (formState.province || '')) {
            setFields({ province: next, city: '' });
            return;
        }
        setFields({ province: next });
    };

    return (
        <div>
            <div className={showProfileFields ? profileClasses.basicLayout : undefined}>
            {showProfileFields && (
                <ProfilePhotoField
                    variant="compact"
                    src={formState.profile_image}
                    inputId={photoInputId}
                    onChange={handlePhoto}
                    onRemove={() => handleInputChange({ target: { name: 'profile_image', value: '' } })}
                />
            )}
            <div className={profileClasses.fieldsCol}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        label="First Name *"
                        name="firstName"
                        value={formState.firstName}
                        type="text"
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        label="Surname *"
                        name="lastName"
                        value={formState.lastName}
                        type="text"
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        label="Username *"
                        name="userName"
                        value={formState.userName}
                        placeholder="Username (same as Email)"
                        disabled={true}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        label="Email *"
                        name="email"
                        value={formState.email}
                        type="email"
                        disabled={!!emailDisable}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        multiple
                        id="tags-outlined"
                        value={customers}
                        name="customers"
                        options={companyList || []}
                        getOptionLabel={option => option.name}
                        onChange={(event, newValue) => {
                            handleChangeCustomer(newValue, 'customers');
                        }}
                        limitTags={2}
                        fullWidth
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                            <Chip variant="outlined" size="small" label={option.name} {...getTagProps({ index })} />
                        ))}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                fullWidth
                                label="Company Name *"
                            />
                        )}
                    />
                </Grid>
                {roleList && roleList.length &&
                    <Grid item xs={12} md={4}>
                        <OutlinedSelect
                            val={formState.role}
                            name="role"
                            label="Role *"
                            variant="outlined"
                            handleChange={handleInputChange}
                            styleOverrides={{ width: '100%', marginTop: 0 }}
                            options={roleList}
                            disabled={!!adminFieldsLocked}
                        />
                    </Grid>
                }
                {!(roleList && roleList.length) &&
                    <Grid item xs={12} md={4}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            size="small"
                            label="Location *"
                            name="location"
                            value={formState.location}
                            type="text"
                            onChange={handleInputChange}
                        />
                    </Grid>
                }
            </Grid>
            <Grid container spacing={3}>
                {roleList && roleList.length && ['superadmin', 'customeradmin', 'projectadmin'].includes(formState.role) &&
                    ['superadmin', 'customeradmin'].includes(userDetails?.role) &&
                    <Grid item xs={12} md={4}>
                        <OutlinedSelect
                            val={formState.sub_role}
                            name="sub_role"
                            label="Secondary Role *"
                            variant="outlined"
                            handleChange={(evt) => handleInputChange(evt)}
                            styleOverrides={{ width: '100%', marginTop: 0 }}
                            options={[{ id: 'gcologist', name: 'GCologist' }]}
                            disabled={!!adminFieldsLocked}
                        />
                    </Grid>
                }
                {roleList && roleList.length &&
                    <Grid item xs={12} md={4}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            size="small"
                            label="Location *"
                            name="location"
                            value={formState.location}
                            type="text"
                            onChange={handleInputChange}
                        />
                    </Grid>
                }
                {((userDetails?.role === USER_ROLES.SUPER_ADMIN || userDetails?.role === USER_ROLES.CUSTOMER_ADMIN) || showProject) && projectList && !isOnbaording &&
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            value={formState.project || null}
                            options={projectList || []}
                            getOptionLabel={(option) => (option && option.name) || ''}
                            onChange={(event, newValue) => handleChangeCustomer(newValue, 'project')}
                            fullWidth
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    label="Project *"
                                />
                            )}
                        />
                    </Grid>
                }
                {showPassword && !adminFieldsLocked &&
                    <Grid item xs={12} md={4}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            size="small"
                            label="Password"
                            name="password"
                            value={formState.password || ''}
                            type="text"
                            onChange={handleInputChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={onGeneratePassword}
                                            style={{ color: '#fff', textTransform: 'none' }}
                                        >
                                            Generate
                                        </Button>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                }
            </Grid>
            </div>
            </div>
            {showProfileFields && (
                <>
                    <div className={profileClasses.section}>
                        <SectionHeader
                            classes={profileClasses}
                            icon={<ContactPhoneOutlinedIcon />}
                            title="Additional profile information"
                            hint="Optional. Contact, location and background details."
                        />
                        <Grid container spacing={3} className={profileClasses.sectionFields}>
                            <Grid item xs={12} md={4}>
                                <PhoneInputField
                                    label="Contact Number"
                                    required={false}
                                    value={String(formState.contact_no || '').replace(/^\+/, '')}
                                    handleChange={(value) => setFields({
                                        contact_no: value ? `+${String(value).replace(/^\+/, '')}` : '',
                                    })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <ProfileAutocomplete
                                    label="Country"
                                    value={formState.country || ''}
                                    options={countryOptions}
                                    onChange={handleCountryChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <ProfileAutocomplete
                                    label="Province"
                                    value={formState.province || ''}
                                    options={provinces}
                                    onChange={handleProvinceChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <ProfileAutocomplete
                                    label="City"
                                    value={formState.city || ''}
                                    options={withCurrentOption([], formState.city)}
                                    onChange={(next) => setFields({ city: next })}
                                />
                            </Grid>
                            {TEXT_PROFILE_FIELDS.map((field) => (
                                <ProfileTextField
                                    key={field.name}
                                    field={field}
                                    value={formState[field.name]}
                                    onChange={handleFieldChange}
                                />
                            ))}
                        </Grid>
                    </div>
                    <div className={profileClasses.section}>
                        <SectionHeader
                            classes={profileClasses}
                            icon={<WorkOutlineIcon />}
                            title={programmeType ? `${programmeTypeLabel || 'Programme'} profile` : 'Reskilling & employment information'}
                            hint={programmeType ? 'Fields that apply to this project’s programme.' : 'Job details are shown for employed reskilling learners.'}
                        />
                        <Grid container spacing={3} className={profileClasses.sectionFields}>
                        {programmeType ? programmeProfileFields.map((field) => (
                            <ProfileTextField
                                key={field.name}
                                field={field}
                                value={formState[field.name]}
                                onChange={handleFieldChange}
                            />
                        )) : (
                        <>
                        <Grid item xs={12} md={4}>
                            <OutlinedSelect
                                val={formState.learner_category || ''}
                                name="learner_category"
                                label="Learner Category"
                                variant="outlined"
                                handleChange={handleFieldChange}
                                styleOverrides={{ width: '100%', marginTop: 0 }}
                                options={LEARNER_CATEGORY_OPTIONS}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <OutlinedSelect
                                val={formState.employment_status || ''}
                                name="employment_status"
                                label="Employment Status"
                                variant="outlined"
                                handleChange={handleFieldChange}
                                styleOverrides={{ width: '100%', marginTop: 0 }}
                                options={EMPLOYMENT_STATUS_OPTIONS}
                            />
                        </Grid>
                        {showJobFields && JOB_PROFILE_FIELDS.map((field) => (
                            <ProfileTextField
                                key={field.name}
                                field={field}
                                value={formState[field.name]}
                                onChange={handleFieldChange}
                            />
                        ))}
                        </>
                        )}
                        </Grid>
                    </div>
                </>
            )}
        </div>
    );
}

function ProfileAutocomplete({ label, value, options, onChange, noOptionsText }) {
    const optionList = options || [];
    return (
        <Autocomplete
            freeSolo
            autoHighlight
            noOptionsText={noOptionsText}
            options={optionList}
            // Use `inputValue` (not `value`) to carry the typed text. If `value`
            // tracked every keystroke, MUI would treat the input as already
            // matching a "selected" option on every render and stop filtering
            // the dropdown (it always falls back to an empty-string filter,
            // showing every option regardless of what's typed).
            inputValue={value || ''}
            onChange={(event, newValue, reason) => {
                const raw = typeof newValue === 'string' ? newValue : '';
                // On blur/commit, snap case-insensitive matches to the canonical option
                // (e.g. typing "south africa" becomes "South Africa") so the value is
                // recognised everywhere downstream that compares against the option list.
                const normalized = (reason === 'blur' || reason === 'create-option')
                    ? normalizeToOption(raw, optionList)
                    : raw;
                onChange(normalized);
            }}
            onInputChange={(event, newValue, reason) => {
                if (reason === 'input' || reason === 'clear') onChange(newValue || '');
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    fullWidth
                    label={label}
                />
            )}
        />
    );
}

function ProfileTextField({ field, value, onChange }) {
    const isDate = field.type === 'date';
    return (
        <Grid item xs={12} md={field.fullWidth ? 12 : 4}>
            <TextField
                variant="outlined"
                fullWidth
                size="small"
                label={field.label}
                name={field.name}
                value={value ?? ''}
                type={isDate ? 'date' : 'text'}
                multiline={!!field.multiline}
                minRows={field.multiline ? 3 : undefined}
                onChange={onChange}
                inputProps={{
                    maxLength: field.maxLength,
                    inputMode: field.type === 'integer' ? 'numeric' : undefined,
                    max: isDate ? formatLocalIsoDate(new Date()) : undefined,
                }}
                InputLabelProps={isDate ? { shrink: true } : undefined}
            />
        </Grid>
    );
}
